import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

type ThemePreference = 'system' | 'light' | 'dark';

const THEME_STORAGE_KEY = 'skiloope:theme';
const parseTheme = (value: string | null): ThemePreference =>
  value === 'light' || value === 'dark' ? value : 'system';
const readTheme = (): ThemePreference => {
  try {
    return parseTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
};

const ThemeContext = createContext<{
  preference: ThemePreference;
  setPreference: (value: ThemePreference) => void;
} | null>(null);

const ThemeProvider: FC = ({ children }) => {
  const [preference, setTheme] = useState(readTheme);
  const setPreference = useCallback((value: ThemePreference): void => {
    setTheme(value);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch {
      // Keep the choice for this page when browser storage is unavailable.
    }
  }, []);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', preference);
    document.documentElement.style.colorScheme =
      preference === 'system' ? 'light dark' : preference;
  }, [preference]);

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      if (event.key === THEME_STORAGE_KEY || event.key === null)
        setTheme(parseTheme(event.newValue));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({ preference, setPreference }),
    [preference, setPreference],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

const useTheme = (): NonNullable<React.ContextType<typeof ThemeContext>> => {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme requires ThemeProvider');
  return value;
};

export { ThemeProvider, useTheme };
